/* ══════════════════════════════════════════════════════════════════════
   Splash — collection page behaviour

   Phase 1: filter chips, sort, add-to-cart (bumps the coral cart badge).
   Phase 2 seams, active only when the flag rendered the components:
     • fit toggle — filters out won't-fit items
     • natural-language filter — currently forwards to Splash.Ripple
     • "Why it won't fit" — opens the explanation

   Filtering is one pipeline, not two: chips and the fit toggle both feed
   applyFilters(), so adding the Phase 2 control did not fork the Phase 1
   logic.
   ══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var Splash = (window.Splash = window.Splash || {});

  function init(root) {
    if (!root || root.dataset.spInit === 'true') return;
    root.dataset.spInit = 'true';

    var grid = root.querySelector('[data-grid]');
    var empty = root.querySelector('[data-empty]');
    var chips = [].slice.call(root.querySelectorAll('[data-chip]'));
    var sort = root.querySelector('[data-sort]');
    var fitToggle = root.querySelector('[data-fit-toggle] input');
    var cards = function () {
      return [].slice.call(grid.querySelectorAll('[data-product-card]'));
    };

    /* ── Filtering ─────────────────────────────────────────────────── */

    function activeFacets() {
      var facets = {};
      chips.forEach(function (chip) {
        if (chip.getAttribute('aria-pressed') !== 'true') return;
        var facet = chip.dataset.facet;
        (facets[facet] = facets[facet] || []).push(chip.dataset.value);
      });
      return facets;
    }

    function matches(card, facets) {
      for (var facet in facets) {
        if (!Object.prototype.hasOwnProperty.call(facets, facet)) continue;
        var values = facets[facet];
        var ok = false;
        for (var i = 0; i < values.length; i++) {
          var v = values[i];
          if (facet === 'type') ok = ok || card.dataset.type === v;
          else if (facet === 'brand') ok = ok || card.dataset.brand === v;
          else if (facet === 'price') ok = ok || Number(card.dataset.price) <= Number(v);
        }
        if (!ok) return false;
      }
      return true;
    }

    function applyFilters() {
      var facets = activeFacets();
      var hideUnfit = !!(fitToggle && fitToggle.checked);
      var shown = 0;

      cards().forEach(function (card) {
        var visible = matches(card, facets);
        if (visible && hideUnfit && card.dataset.fit === 'wont') visible = false;
        card.hidden = !visible;
        if (visible) shown++;
      });

      if (empty) empty.hidden = shown > 0;
      syncFitToggle();
    }

    /* The switch must not claim to be filtering when won't-fit items are on
       screen. Its rendered state is derived from the list, never assumed. */
    function syncFitToggle() {
      if (!fitToggle) return;
      var unfitVisible = cards().some(function (card) {
        return !card.hidden && card.dataset.fit === 'wont';
      });
      if (unfitVisible && fitToggle.checked) fitToggle.checked = false;
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var on = chip.getAttribute('aria-pressed') === 'true';
        chip.setAttribute('aria-pressed', on ? 'false' : 'true');
        applyFilters();
      });
    });

    if (fitToggle) fitToggle.addEventListener('change', applyFilters);

    /* ── Sort ──────────────────────────────────────────────────────── */

    if (sort) {
      sort.addEventListener('change', function () {
        var mode = sort.value;
        var items = cards();
        /* The insight card holds the first cell and must stay there. */
        var pinned = grid.querySelector('[data-ripple-insight]');

        items.sort(function (a, b) {
          if (mode === 'price-asc') return num(a, 'price') - num(b, 'price');
          if (mode === 'price-desc') return num(b, 'price') - num(a, 'price');
          if (mode === 'rating') return text(b, 'rating') - text(a, 'rating');
          if (mode === 'fit') return fitRank(a) - fitRank(b);
          return 0; /* "most popular" = the authored order */
        });

        if (mode !== 'popular') {
          items.forEach(function (card) {
            grid.appendChild(card);
          });
        }
        if (pinned) grid.insertBefore(pinned, grid.firstChild);
      });
    }

    function num(card, key) {
      return Number(card.dataset[key]) || 0;
    }
    function text(card) {
      var el = card.querySelector('.sp-card__rating');
      var m = el && el.textContent.match(/([0-9.]+)/);
      return m ? Number(m[1]) : 0;
    }
    function fitRank(card) {
      return { fits: 0, adapter: 1, owned: 2, wont: 3 }[card.dataset.fit] ?? 1;
    }

    /* ── Add to cart ───────────────────────────────────────────────── */

    root.addEventListener('click', function (event) {
      var add = event.target.closest('[data-add-to-cart]');
      if (add) {
        event.preventDefault();
        bumpCart(1);
        add.textContent = 'Added';
        setTimeout(function () {
          add.textContent = 'Add';
        }, 1400);
        return;
      }

      var why = event.target.closest('[data-why-not]');
      if (why) {
        event.preventDefault();
        if (Splash.Ripple) {
          Splash.Ripple.open({ source: 'why-it-wont-fit', reason: why.dataset.whyNot });
        }
      }
    });

    /* Cart count lives in the header; this only nudges it. */
    function bumpCart(by) {
      var badge = document.querySelector('[data-cart-count]');
      var next = (Number(badge && badge.textContent) || 0) + by;
      if (Splash.Header && Splash.Header.setCartCount) Splash.Header.setCartCount(next);
      else if (badge) {
        badge.textContent = String(next);
        badge.hidden = false;
      }
    }

    /* ── Phase 2 · natural-language filter ─────────────────────────── */

    var nl = root.querySelector('[data-nl-filter]');
    if (nl) {
      nl.addEventListener('submit', function (event) {
        event.preventDefault();
        var value = nl.querySelector('input').value.trim();
        if (!value) return;
        /* Server-side narrowing is Ripple's job; the seam is the handler. */
        if (Splash.Ripple) Splash.Ripple.query(value, { source: 'collection-nl-filter' });
      });
    }

    var dismiss = root.querySelector('[data-insight-dismiss]');
    if (dismiss) {
      dismiss.addEventListener('click', function () {
        var card = root.querySelector('[data-ripple-insight]');
        if (card) card.remove();
      });
    }

    applyFilters();
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-collection]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
